import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { BaseOffer, OfferBlock } from './OfferBlock';
import { Recurrence, useOffers, useCountryPriceTiers } from '@app/api';
import { OfferActionProvider } from './OfferActionProvider';
import classNames from 'classnames';
import { getOfferProductOrdersInfos } from '../../lib/products';

const useStyles = createUseStyles((theme: Theme) => ({
  columns: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: theme.marginBase * 3,

    [theme.media.mobile]: {
      flexDirection: 'column',
    },
  },
  columnsWithMostPopular: {
    paddingTop: theme.marginBase * 4,
  },
}));

interface Props<T extends BaseOffer> {
  offers?: Array<T>;
  name?: string;
  onClick?: (offer: T) => any;
  duration?: number;
  category?: string;
  recurrence?: Recurrence;
  selectedId?: string;
  disableDownsell?: boolean;
  hideCriteriaDetails?: boolean;
  countryKeys?: string[];
}

export const OfferList = function <T extends BaseOffer>({
  offers: partialOffers,
  name,
  duration,
  onClick,
  category,
  recurrence,
  selectedId,
  disableDownsell,
  hideCriteriaDetails,
  countryKeys,
}: Props<T>) {
  const classes = useStyles();

  const countryPriceTiers = useCountryPriceTiers(countryKeys);

  const { data: allOffers } = useOffers();
  const offers = useMemo(() => {
    if (!allOffers) {
      return partialOffers;
    }

    if (!partialOffers) {
      return null;
    }
    return allOffers
      .filter((o) => partialOffers.some((p) => p.id === o.id))
      .sort((a, b) => {
        if (a.requireCallToSales) {
          return 1;
        }
        if (b.requireCallToSales) {
          return -1;
        }

        const priceA = Object.values(a.prices).reduce((acc, p) => acc + p, 0);
        const priceB = Object.values(b.prices).reduce((acc, p) => acc + p, 0);
        return priceA - priceB;
      });
  }, [partialOffers, allOffers]);

  const offerProductOrdersInfos = useMemo(
    () => getOfferProductOrdersInfos(offers || []),
    [offers],
  );

  const disabledOffers = useMemo(() => {
    if (!disableDownsell) {
      return [];
    }
    const res = [];
    for (const o of offers || []) {
      if (selectedId === o.id) {
        return res;
      }
      res.push(o.id);
    }
    // not found
    return [];
  }, [offers, disableDownsell, selectedId]);

  if (!offers) {
    return null;
  }

  return (
    <div
      className={classNames(classes.columns, {
        [classes.columnsWithMostPopular]: offers.some(
          (offer) => 'mostPopular' in offer && offer.mostPopular,
        ),
      })}
    >
      {offers.map((offer) => (
        <OfferActionProvider
          name={name}
          offerId={offer.id}
          key={offer.id}
          onClick={onClick}
          selected={selectedId}
          defaultSubscription={
            'defaultSubscription' in offer && !!offer.defaultSubscription
          }
        >
          {({ onClick, selected, selectMode }) => (
            <OfferBlock
              key={offer.id}
              offer={offer as T}
              duration={duration}
              onClick={onClick}
              selected={selected}
              recurrence={recurrence}
              category={category}
              selectMode={selectMode}
              offerProductOrdersInfos={offerProductOrdersInfos}
              disabled={disabledOffers.includes(offer.id)}
              hideCriteriaDetails={hideCriteriaDetails}
              countryPriceTiers={countryPriceTiers}
              countryKeys={countryKeys}
            />
          )}
        </OfferActionProvider>
      ))}
    </div>
  );
};
